import React from 'react';
import styled from 'styled-components';

import { LayoutContext } from 'contexts';

import dateFnsGenerateConfig from 'rc-picker/lib/generate/dateFns';
import generatePicker, {
  PickerProps,
  PickerDateProps,
} from 'antd/es/date-picker/generatePicker';
import { useBreakpoint } from 'hooks';

import { defer } from 'lodash';
import LabelWrapper from '../LabelWrapper';
import theme from '../theme';

export const AntdDatePicker = generatePicker<Date>(dateFnsGenerateConfig);

export type DatePickerProps = PickerProps<Date> & {
  label?: React.ReactNode;
  error?: string | boolean;
  required?: boolean;
  showNow?: PickerDateProps<Date>['showNow'];
  showToday?: PickerDateProps<Date>['showToday'];
  showTime?: PickerDateProps<Date>['showTime'];
  disabledTime?: PickerDateProps<Date>['disabledTime'];
};

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  error,
  required,
  getPopupContainer,
  allowClear = false, // For consistency with other components
  className,
  ...props
}) => {
  const layout = React.useContext(LayoutContext);
  const prevRef = React.useRef<HTMLSpanElement>(null);
  const nextRef = React.useRef<HTMLSpanElement>(null);
  const { md } = useBreakpoint();

  const getContainerFunction = () => {
    if (getPopupContainer) return getPopupContainer;
    if (layout?.scrollContainer) return () => layout.scrollContainer;
    return null;
  };

  return (
    <LabelWrapper
      label={label}
      error={error}
      required={required}
      className={className}
    >
      <StyledDatePicker
        prevIcon={<span ref={prevRef} className="ant-picker-prev-icon" />}
        nextIcon={<span ref={nextRef} className="ant-picker-next-icon" />}
        {...props}
        onOpenChange={(...open) => {
          defer(() => {
            prevRef.current?.parentElement?.setAttribute(
              'data-cy',
              'previous-month-button',
            );
            nextRef.current?.parentElement?.setAttribute(
              'data-cy',
              'next-month-button',
            );
          });
          return props.onOpenChange?.(...open);
        }}
        getPopupContainer={getContainerFunction()}
        allowClear={allowClear}
        inputReadOnly={!md} // only disable on small screens so cypress can still edit the field for automation
      />
    </LabelWrapper>
  );
};

const StyledDatePicker = styled(AntdDatePicker)`
  background-color: ${theme.gray3};
`;

export default styled(DatePicker)``;
